import React from "react";
import Banner from "components/Banner";
import Addons from "components/Addons";
import PlanDetails from "components/PlanDetails";
import PlanPrice from "components/PlanPrice";
import PromoCodeBenefits from "components/PromoCodeBenefits";
import { Box, Text } from "ui-components";
import { iconThemes, COLORS } from "ui-components/theme";
import get from "lodash/get";
import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import { PREPAID_PLAN_SUB_TYPES } from "utils/constants";
import FormattedMessage from "../FormattedMessage";
import { formatGBtoTBConversion } from "utils/helpers";

const PlanDetailsCard = ({
  planDetails,
  payTodayItems,
  payMonthlyItems,
  data = {},
  themeColors,
  appSettings,
  addons,
  defaultPlanAddons,
  referralCode,
  defaultAddons,
  eligiblePlans,
  setSectionListModal
}) => {
  const planName = get(planDetails, "name", "");
  const planSubType = get(planDetails, "sub_type", "");
  const productLink = get(planDetails, "product_link");
  const isPrepaid = PREPAID_PLAN_SUB_TYPES.includes(planSubType);
  const paySummaryItems = isPrepaid ? payTodayItems : payMonthlyItems;
  const planItem = find(get(paySummaryItems, "items"), { type: "plan" });
  const showSummary = get(data, "showSummary");
  const excludeTax = get(data, "excludeTax");
  const defaultAddonsTitle = get(
    data,
    "defaultAddonsTitle",
    "default_addons_title"
  );
  const planPrice = excludeTax
    ? get(planItem, "price_without_tax")
    : get(planItem, "price");
  const totalPrice = excludeTax
    ? get(paySummaryItems, "totalPriceWithoutTax")
    : get(paySummaryItems, "totalPrice");
  const planMessage = get(data, "message", "");
  const fitPillContent = get(data, "fitPillContent", false);
  const hidePlanPrice = get(data, "hidePlanPrice", false);
  const description = get(data, "description", "");
  const planColors = get(data, ["planColors", planName]) || {};
  const primaryColor =
    get(iconThemes, [planColors.primary]) ||
    get(themeColors, "card") ||
    get(themeColors, "primary") ||
    iconThemes.blue;
  const priceColor =
    get(iconThemes, [planColors.price], planColors.price) ||
    get(data, "priceColor", COLORS.white);
  const tertiaryColor =
    get(iconThemes, [planColors.tertiary]) ||
    get(themeColors, "tertiary") ||
    iconThemes.cyan;
  const iconColor =
    get(iconThemes, [planColors.icon], planColors.icon) ||
    get(themeColors, "icon") ||
    iconThemes.blue;
  const totalData = get(paySummaryItems, "totalData");
  const showGbConversionToTbEnabled = get(
    appSettings,
    "generic.show_gb_conversion_to_tb_enabled",
    false
  );
  if (paySummaryItems && showGbConversionToTbEnabled) {
    paySummaryItems.totalDataTB =
      totalData > 0 ? formatGBtoTBConversion(totalData) : 0;
  }
  const activePromoCode = get(referralCode, "valid")
    ? get(referralCode, "value")
    : "";
  const borderWidth = get(data, "borderWidth", 6);
  const items = get(paySummaryItems, "items");

  // For Border-less Plan
  const planCountries = payMonthlyItems?.planCountries;
  const isBorderlessPlan = payMonthlyItems?.isBorderlessPlan;
  const planOriginalPrice = payMonthlyItems?.originalPrice;
  const selectedBorderlessAddons = payMonthlyItems?.selectedBorderlessAddons;

  let bordlessPlanDetails;
  if (isBorderlessPlan) {
    let totalBorderlessGB = 0;
    const totalData = payMonthlyItems?.totalData;
    selectedBorderlessAddons.forEach(addon => {
      totalBorderlessGB += addon?.borderless_gb || 0;
    });
    const borderlessData =
      totalData >= totalBorderlessGB ? totalBorderlessGB : totalData;
    const pay_monthly = [
      {
        label: `${borderlessData} GB across`,
        type: "base"
      },
      {
        label: `${payMonthlyItems?.totalMins} mins local talktime`,
        type: "base"
      },
      { label: "local_sms", type: "base" },
      { label: "free_caller_number_display", type: "base" },
      { label: "free_incoming_roaming_sms", type: "base" }
    ];
    bordlessPlanDetails = { ...planDetails, pay_monthly };
    addons = addons.filter(a => !a.name?.toLowerCase().includes("borderless"));
  }

  return (
    <Box
      variant="borderSection"
      mb={8}
      px={[0, 0]}
      py={[2, 2]}
      sx={{
        borderWidth,
        color: primaryColor,
        borderColor: primaryColor
      }}
    >
      <Banner
        types={["promoBanners", "dataBanners", "planBanners"]}
        typeValues={{
          promoBanners: activePromoCode,
          dataBanners: totalData,
          planBanners: planName
        }}
        data={data}
        appSettings={appSettings}
      />
      <PlanDetails
        planDetails={
          !isEmpty(planCountries) ? bordlessPlanDetails : planDetails
        }
        items={items}
        primaryColor={iconColor}
        planTotalData={totalData}
        setSectionListModal={setSectionListModal}
        planCountries={planCountries}
        planOriginalPrice={planOriginalPrice}
        {...data}
      />
      {showSummary && (
        <>
          <Addons
            data-testid="default-addons"
            addons={defaultPlanAddons}
            primaryColor={iconColor}
            excludeTax={excludeTax}
            title={defaultAddonsTitle}
          />
          <Addons
            data-testid="addons"
            addons={addons}
            primaryColor={iconColor}
            excludeTax={excludeTax}
          />
          {!isBorderlessPlan && (
            <PromoCodeBenefits
              data-testid="plan-promos"
              items={get(paySummaryItems, "items")}
              referralCode={referralCode}
              themeColors={themeColors}
            />
          )}
        </>
      )}
      {!hidePlanPrice && (
        <PlanPrice
          id="plan-price"
          price={showSummary ? totalPrice : planPrice}
          totalData={totalData}
          price_label={get(data, "price_label")}
          paySummaryItems={paySummaryItems}
          message={planMessage}
          textColor={priceColor}
          bgColor={primaryColor}
          pillColor={tertiaryColor}
          fitPillContent={fitPillContent}
          isPrepaid={isPrepaid}
          productLink={productLink}
          showGbConversionToTbEnabled={showGbConversionToTbEnabled}
        />
      )}
      {description && (
        <Box mb={4} px={"1rem"}>
          <Box
            px={5}
            py={3}
            backgroundColor={themeColors?.info}
            style={{ borderRadius: "8px" }}
            textAlign="left"
          >
            <Text>
              <FormattedMessage id={description} />
            </Text>
          </Box>
        </Box>
      )}
    </Box>
  );
};

PlanDetailsCard.defaultProps = {
  addons: [],
  planDetails: {},
  payTodayItems: {},
  data: {}
};

export default PlanDetailsCard;
